'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Printer, ScanLine, Trash2, Usb } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { Device, DeviceType } from '../types';

interface DeviceCompactRowProps {
  device: Device;
  onEdit: (device: Device) => void;
  onDelete: (device: Device) => void;
}

const STATUS_STYLES: Record<string, string> = {
  online:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  offline: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400',
  maintenance:
    'border-yellow-500/30 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
};

const TYPE_STYLES: Record<DeviceType, string> = {
  scanner:
    'border-violet-500/30 bg-violet-500/10 text-violet-600 dark:text-violet-400',
  printer: 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  barcode_scanner:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
};

const TYPE_ICON: Record<
  DeviceType,
  React.ComponentType<{ className?: string }>
> = {
  scanner: ScanLine,
  printer: Printer,
  barcode_scanner: Usb
};

export function DeviceCompactRow({
  device,
  onEdit,
  onDelete
}: DeviceCompactRowProps) {
  const t = useTranslations('devices');

  const statusLabel =
    device.status === 'online'
      ? t('statusOnline')
      : device.status === 'offline'
        ? t('statusOffline')
        : t('statusMaintenance');

  const deviceType: DeviceType = device.device_type ?? 'scanner';
  const typeLabel =
    deviceType === 'scanner'
      ? t('deviceTypeBadgeScanner')
      : deviceType === 'printer'
        ? t('deviceTypeBadgePrinter')
        : t('deviceTypeBadgeBarcodeScanner');
  const TypeIcon = TYPE_ICON[deviceType] ?? ScanLine;

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <Badge
        variant='outline'
        className={cn('shrink-0 gap-1', TYPE_STYLES[deviceType])}
      >
        <TypeIcon className='h-3 w-3' />
        <span className='hidden sm:inline'>{typeLabel}</span>
      </Badge>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-medium'>{device.name}</span>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {device.serial_number}
          </span>
        </div>
      </div>

      <span className='text-muted-foreground hidden text-sm md:inline'>
        {device.ip_address}
      </span>

      <Badge
        variant='outline'
        className={cn('shrink-0', STATUS_STYLES[device.status])}
      >
        {statusLabel}
      </Badge>

      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['devices.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(device)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['devices.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='text-destructive h-7 w-7'
            onClick={() => onDelete(device)}
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
